/**
 * An owner can hide a page section without deleting it: the node stays in
 * builderData with `props.isHidden: true`, keeping its words for when they
 * show it again. This is the one place that rule lives; the published build,
 * the editor preview and anything that asks "does this page have a form?"
 * read the page through it.
 */

interface SectionNodeLike {
  props?: Record<string, unknown> & { isHidden?: unknown };
}

const isSectionHidden = (node: SectionNodeLike | null | undefined): boolean => node?.props?.isHidden === true,

  /**
   * The page as visitors see it: top-level sections the owner hid are dropped,
   * order is kept, and `isHidden` is stripped from the rest so a component that
   * spreads its props onto the DOM never receives it as an attribute. Zones are
   * untouched; hiding is a whole-section action.
   */
  visibleSections = <T extends { content?: unknown }>(data: T): T => {
    if (!Array.isArray(data.content)) {
      return data;
    }
    const content = (data.content as SectionNodeLike[])
      .filter((node) => !isSectionHidden(node))
      .map((node) => {
        if (!node?.props || !("isHidden" in node.props)) {
          return node;
        }
        const { isHidden: _isHidden, ...props } = node.props;
        return { ...node, props };
      });
    return { ...data, content };
  };

export { isSectionHidden, visibleSections };
